import { useState } from 'react';
import { Num, Plural, T, useGT } from 'gt-react';
import { DownloadSimpleIcon, TrashIcon, XIcon } from '@phosphor-icons/react';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Button } from '@/components/ui/button';

/**
 * Actions on the files ticked in the Vendor Portals table: download them all, or (with
 * delete permission) delete them for good after a confirmation.
 */
export function VendorPortalSelectionBar({
  count,
  canDelete,
  isDeleting,
  onDownload,
  onDelete,
  onClear,
}: {
  count: number;
  canDelete: boolean;
  isDeleting: boolean;
  onDownload: () => void;
  onDelete: () => Promise<void>;
  onClear: () => void;
}) {
  const gt = useGT();
  const [confirming, setConfirming] = useState(false);

  async function handleConfirm() {
    await onDelete();
    setConfirming(false);
  }

  if (count === 0) return null;
  return (
    <div className="ml-auto flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">
        <T context="Vendor portals count of selected files">
          <Num>{count}</Num> selected
        </T>
      </span>
      <Button
        size="sm"
        variant="outline"
        disabled={isDeleting}
        onClick={onDownload}
      >
        <DownloadSimpleIcon className="h-4 w-4" />
        <T context="Vendor portals button that downloads every selected file">
          Download <Num>{count}</Num>{' '}
          <Plural n={count} one="file" other="files" />
        </T>
      </Button>
      {canDelete && (
        <Button
          size="sm"
          variant="outline"
          className="text-destructive hover:text-destructive"
          disabled={isDeleting}
          onClick={() => setConfirming(true)}
        >
          <TrashIcon className="h-4 w-4" />
          <T context="Vendor portals button that deletes every selected file">
            Delete <Num>{count}</Num>{' '}
            <Plural n={count} one="file" other="files" />
          </T>
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={onClear}>
        <XIcon className="h-4 w-4" />
        <T context="Vendor portals button that clears the file selection">
          Clear
        </T>
      </Button>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void handleConfirm()}
        closeOnConfirm={false}
        dangerous
        loading={isDeleting}
        title={gt(
          '{count, plural, one {Delete # file permanently?} other {Delete # files permanently?}}',
          {
            context: 'Vendor portals delete confirmation dialog title',
            count,
          },
        )}
        message={gt(
          'The files are removed from storage and from Gently and cannot be recovered. Files that other records still use are kept.',
          { context: 'Vendor portals delete confirmation dialog message' },
        )}
        confirmText={gt('Delete permanently', {
          context: 'Vendor portals delete confirmation button',
        })}
        cancelText={gt('Cancel', {
          context: 'Vendor portals delete cancel button',
        })}
      />
    </div>
  );
}
